import { useEffect, useState } from 'react';
import axios from 'axios';
import { Ghost, Skull, Calendar, X, ShieldAlert } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { openBookingModal } from './BookingModal';
import { openHHWaiver } from './HHWaiverModal';
import WaiverStampIcon from './WaiverStampIcon';

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'halloween_dismissed_until';
const DISMISS_SECONDS = 30; // banner reappears after this many seconds

export default function HalloweenBanner() {
  const { lang } = useLanguage();
  const isJa = lang === 'ja';
  const tr = (en, ja) => (isJa ? (ja || en) : (en || ja || ''));
  const [cfg, setCfg] = useState(null);
  const [tick, setTick] = useState(0);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      return until && Date.now() < until;
    } catch { return false; }
  });
  // Alternating icon: false = skull, true = X — flips every ~900ms so users see both
  const [showX, setShowX] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setShowX(v => !v), 900);
    return () => clearInterval(t);
  }, []);

  // Auto-restore the banner after DISMISS_SECONDS — poll every 2s
  useEffect(() => {
    if (!dismissed) return;
    const t = setInterval(() => {
      try {
        const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
        if (!until || Date.now() >= until) {
          setDismissed(false);
          try { localStorage.removeItem(DISMISS_KEY); } catch {}
        }
      } catch {}
    }, 2000);
    return () => clearInterval(t);
  }, [dismissed]);

  // Live "guests disappeared" counter — starts from server count + optimistic increment per scare
  const [disappearedCount, setDisappearedCount] = useState(null);
  const [countJustBumped, setCountJustBumped] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/halloween/disappeared-count`)
      .then(r => setDisappearedCount(r.data?.count || 0))
      .catch(() => {});
  }, []);

  // Any jump scare un-dismisses the banner immediately — victims can't hide from Halloween
  useEffect(() => {
    const onScare = () => {
      try { localStorage.removeItem(DISMISS_KEY); } catch {}
      setDismissed(false);
      // Optimistic bump for the disappeared counter
      setDisappearedCount(c => (c == null ? c : c + 1));
      setCountJustBumped(true);
      setTimeout(() => setCountJustBumped(false), 900);
    };
    window.addEventListener('halloween-scare-fired', onScare);
    return () => window.removeEventListener('halloween-scare-fired', onScare);
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/settings/site`).then(r => setCfg(r.data)).catch(() => {});
  }, []);

  // Log a banner_view once per session (backend dedupes per IP/day anyway)
  const [viewLogged, setViewLogged] = useState(false);
  useEffect(() => {
    if (!cfg || !cfg.halloween_enabled || dismissed || viewLogged) return;
    axios.post(`${API}/api/track/halloween-event`, { event: 'banner_view' }).catch(() => {});
    setViewLogged(true);
  }, [cfg, dismissed, viewLogged]);

  // Re-render countdown every 60s so the "X days" stays fresh
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 60000);
    return () => clearInterval(t);
  }, []);

  if (!cfg || !cfg.halloween_enabled) return null;
  if (dismissed) return null;

  // ---- Countdown / auto-hide logic ----
  const parseMD = (s, fallback) => {
    const m = /^(\d{1,2})-(\d{1,2})$/.exec((s || '').trim());
    return m ? { month: +m[1], day: +m[2] } : fallback;
  };
  const target = parseMD(cfg.halloween_target_date, { month: 10, day: 31 });
  const hideAfter = parseMD(cfg.halloween_hide_after, { month: 11, day: 3 });
  const now = new Date();
  const year = now.getFullYear();

  const buildDate = (md, y) => new Date(y, md.month - 1, md.day, 0, 0, 0, 0);
  const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

  // Hide entirely after the "hide_after" date has passed for this year
  const hideCutoff = endOfDay(buildDate(hideAfter, year));
  if (now > hideCutoff) return null; // auto-hides on Nov 3+ (of current year)

  // Days until target Halloween (roll to next year if we're already past)
  let targetDate = buildDate(target, year);
  if (endOfDay(targetDate) < now) {
    targetDate = buildDate(target, year + 1);
  }
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysUntil = Math.ceil((targetDate - now) / msPerDay);

  const countdownLabel = daysUntil <= 0
    ? (isJa ? 'ハロウィン当日' : "IT'S HALLOWEEN NIGHT")
    : daysUntil === 1
      ? (isJa ? 'ハロウィンまで あと1日' : '1 DAY UNTIL HALLOWEEN')
      : (isJa ? `ハロウィンまで あと${daysUntil}日` : `${daysUntil} DAYS UNTIL HALLOWEEN`);

  const handleClick = () => {
    axios.post(`${API}/api/track/halloween-event`, { event: 'cta_click' }).catch(() => {});
    // Book the Fright always opens the Haunted House Weekday/Weekend ticket picker
    openBookingModal('halloween-disclaimer');
  };

  const handleDismiss = () => {
    axios.post(`${API}/api/track/halloween-event`, { event: 'banner_dismiss' }).catch(() => {});
    try {
      const until = Date.now() + DISMISS_SECONDS * 1000;
      localStorage.setItem(DISMISS_KEY, String(until));
    } catch {}
    setDismissed(true);
  };

  const gifs = Array.isArray(cfg.halloween_gifs) ? cfg.halloween_gifs.slice(0, 3) : [];

  return (
    <>
      {/* Sticky top banner (title + CTA) — follows user through the whole page */}
      <section
        data-testid="halloween-banner"
        className="halloween-banner relative overflow-hidden border-y-2 border-orange-500/60 mt-16 sm:mt-32 sticky top-16 sm:top-32 z-40"
        style={{
          background: 'linear-gradient(90deg, #1a0500 0%, #3d0a00 50%, #1a0500 100%)',
        }}
      >
        <div className="pointer-events-none absolute inset-0 halloween-flicker" />

        {/* Blood drip overlay across the top edge — active while the banner is visible */}
        <div
          data-testid="halloween-blood-drip"
          className="pointer-events-none absolute top-0 left-0 right-0 z-20"
          aria-hidden="true"
        >
          <svg
            viewBox="0 0 1440 100"
            preserveAspectRatio="none"
            className="w-full h-16 sm:h-20 lg:h-24 block halloween-blood-svg"
          >
            <defs>
              <linearGradient id="bloodGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#5a0000" />
                <stop offset="55%" stopColor="#8b0000" />
                <stop offset="100%" stopColor="#c40000" />
              </linearGradient>
              <filter id="bloodDropShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="1.5" />
                <feOffset dx="0" dy="1" result="offsetblur" />
                <feFlood floodColor="#3a0000" floodOpacity="0.9" />
                <feComposite in2="offsetblur" operator="in" />
                <feMerge>
                  <feMergeNode />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            {/* Main drip body — irregular liquid edge across full viewport width */}
            <path
              d="
                M0,0
                L1440,0
                L1440,32
                C1420,36 1400,40 1380,38
                C1360,36 1345,44 1330,50
                C1315,56 1300,54 1280,44
                C1260,34 1240,38 1220,46
                C1200,54 1180,58 1160,50
                C1145,44 1130,42 1115,56
                C1100,70 1085,68 1070,52
                C1055,36 1035,38 1020,44
                C1000,52 985,50 970,40
                C955,30 935,34 918,44
                C900,54 880,56 862,46
                C845,36 828,40 810,52
                C790,66 770,64 752,52
                C735,42 715,46 700,58
                C680,74 660,72 642,60
                C625,50 605,54 588,64
                C570,74 550,72 532,56
                C515,42 495,46 478,58
                C458,72 438,68 420,52
                C405,40 385,42 368,54
                C350,66 330,64 312,50
                C295,36 275,40 258,52
                C240,66 220,64 202,50
                C185,38 165,42 148,54
                C130,66 110,64 92,48
                C77,36 57,40 40,52
                C22,62 8,58 0,50
                Z
              "
              fill="url(#bloodGradient)"
              filter="url(#bloodDropShadow)"
            />
            {/* Standalone drop teardrops that fall further below */}
            <ellipse cx="140" cy="72" rx="4" ry="9" fill="#a30000" opacity="0.85" className="halloween-drop halloween-drop-0" />
            <ellipse cx="340" cy="66" rx="3.5" ry="7.5" fill="#9a0000" opacity="0.8" className="halloween-drop halloween-drop-1" />
            <ellipse cx="560" cy="76" rx="4.5" ry="10" fill="#b30000" opacity="0.9" className="halloween-drop halloween-drop-2" />
            <ellipse cx="800" cy="62" rx="3" ry="6" fill="#8b0000" opacity="0.75" className="halloween-drop halloween-drop-3" />
            <ellipse cx="1000" cy="74" rx="4" ry="8.5" fill="#a30000" opacity="0.85" className="halloween-drop halloween-drop-4" />
            <ellipse cx="1220" cy="68" rx="3.5" ry="7" fill="#9a0000" opacity="0.8" className="halloween-drop halloween-drop-5" />
            <ellipse cx="1380" cy="72" rx="4" ry="8" fill="#b30000" opacity="0.9" className="halloween-drop halloween-drop-6" />
          </svg>
        </div>
        <svg className="pointer-events-none absolute -top-4 -left-4 w-24 h-24 text-orange-500/20" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1">
          <path d="M0 0 L100 100 M0 33 L100 100 M0 66 L100 100 M33 0 L100 100 M66 0 L100 100" />
          <path d="M0 0 L20 20 L0 40 M0 0 L40 20 L0 40 M20 20 L40 20 M0 0 L20 40 L40 20" />
        </svg>
        <svg className="pointer-events-none absolute -top-4 -right-4 w-24 h-24 text-orange-500/20 scale-x-[-1]" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1">
          <path d="M0 0 L100 100 M0 33 L100 100 M0 66 L100 100 M33 0 L100 100 M66 0 L100 100" />
          <path d="M0 0 L20 20 L0 40 M0 0 L40 20 L0 40 M20 20 L40 20 M0 0 L20 40 L40 20" />
        </svg>

        {/* Dismiss button — skull↔X flips every ~900ms so customers know it's a close button */}
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="halloween-dismiss-btn"
          title="Hide banner for 30 seconds"
          aria-label="Hide Halloween banner"
          className="halloween-dismiss absolute top-20 right-2 sm:top-24 sm:right-3 lg:top-28 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center z-40"
        >
          {showX
            ? <X size={18} className="text-orange-100 halloween-dismiss-icon" data-testid="halloween-dismiss-icon-x" />
            : <Skull size={18} className="text-orange-300 halloween-dismiss-icon" data-testid="halloween-dismiss-icon-skull" />}
        </button>

        <div className="relative z-30 max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 lg:pt-28 pb-5 sm:pb-8 flex flex-col sm:flex-row items-center gap-3 sm:gap-8">
          <div className="halloween-icon-pulse shrink-0">
            <Ghost
              size={64}
              className="text-orange-400 w-11 h-11 sm:w-16 sm:h-16"
              style={{ filter: 'drop-shadow(0 0 14px rgba(255,140,0,0.95)) drop-shadow(0 0 28px rgba(255,140,0,0.6))' }}
            />
          </div>

          <div className="flex-1 text-center sm:text-left w-full">
            <p className="font-heading text-[26px] leading-[1] sm:text-5xl lg:text-6xl xl:text-7xl tracking-wide uppercase halloween-title sm:leading-[0.95]" data-testid="halloween-title">
              {tr(cfg.halloween_title, cfg.halloween_title_ja)}
            </p>
            <p className="font-sans text-[13px] leading-snug sm:text-base lg:text-lg text-orange-200/95 mt-2 sm:mt-2 tracking-wide">
              {tr(cfg.halloween_subtitle, cfg.halloween_subtitle_ja)}
            </p>
            {(cfg.halloween_dates || cfg.halloween_live_actors) && (
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-1.5 sm:gap-2 mt-2.5 sm:mt-2.5">
                {(cfg.halloween_dates || cfg.halloween_dates_ja) && (
                  <p className="inline-flex items-center gap-1.5 sm:gap-2 font-heading text-[10px] sm:text-sm tracking-widest uppercase text-orange-400/95 leading-none py-1">
                    <Calendar size={12} className="shrink-0 sm:hidden" />
                    <Calendar size={14} className="shrink-0 hidden sm:inline" />
                    {tr(cfg.halloween_dates, cfg.halloween_dates_ja)}
                  </p>
                )}
                {(cfg.halloween_live_actors || cfg.halloween_live_actors_ja) && (
                  <span
                    data-testid="halloween-live-actors"
                    className="halloween-live-actors inline-flex items-center gap-1.5 font-heading text-[9px] sm:text-sm tracking-[0.18em] sm:tracking-[0.2em] uppercase px-2 sm:px-3 py-1 rounded-full leading-none"
                  >
                    <span className="halloween-live-dot" />
                    {tr(cfg.halloween_live_actors, cfg.halloween_live_actors_ja)}
                  </span>
                )}
              </div>
            )}
            {((cfg.halloween_disclaimer || cfg.halloween_disclaimer_ja) || disappearedCount != null) && (
              <div className="flex flex-col sm:flex-row sm:flex-wrap items-center sm:items-baseline gap-1 sm:gap-x-3 sm:gap-y-1 mt-2.5 sm:mt-2 max-w-2xl mx-auto sm:mx-0 text-center sm:text-left">
                {(cfg.halloween_disclaimer || cfg.halloween_disclaimer_ja) && (
                  <p
                    data-testid="halloween-disclaimer"
                    className="halloween-disclaimer font-sans italic text-[10px] leading-tight sm:text-xs lg:text-sm text-orange-200/70"
                  >
                    * {tr(cfg.halloween_disclaimer, cfg.halloween_disclaimer_ja)}
                  </p>
                )}
                {disappearedCount != null && (
                  <span
                    data-testid="halloween-disappeared-count"
                    className={`halloween-disappeared inline-flex items-baseline gap-1 font-heading text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.25em] uppercase ${countJustBumped ? 'halloween-disappeared-bump' : ''}`}
                  >
                    <span className="halloween-disappeared-label">{isJa ? '行方不明のお客様:' : 'Guests Disappeared:'}</span>
                    <span className="halloween-disappeared-num font-mono tracking-normal text-xs sm:text-base">{disappearedCount.toLocaleString()}</span>
                  </span>
                )}
              </div>
            )}
            <div
              data-testid="halloween-countdown"
              className="halloween-countdown-pill mt-3 inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 rounded-full font-heading text-[10px] sm:text-sm lg:text-base tracking-[0.18em] sm:tracking-[0.2em] uppercase"
            >
              <span className="halloween-countdown-dot" />
              {countdownLabel}
            </div>
          </div>

          <button
            onClick={handleClick}
            data-testid="halloween-cta"
            aria-label={tr(cfg.halloween_cta_label, cfg.halloween_cta_label_ja)}
            className={`halloween-ticket-cta halloween-ticket-theme-${cfg.halloween_cta_ticket_theme || 'neon-red'} shrink-0 group relative`}
          >
            {/* Left stub */}
            <span className="halloween-ticket-stub halloween-ticket-stub-left" aria-hidden="true">
              <span className="halloween-ticket-stub-num">{disappearedCount != null ? String(disappearedCount).padStart(6, '0') : '000000'}</span>
            </span>

            {/* Main ticket body with starburst */}
            <span className="halloween-ticket-body">
              <span className="halloween-ticket-burst" aria-hidden="true" />
              <span className="halloween-ticket-star halloween-ticket-star-tl" aria-hidden="true">★</span>
              <span className="halloween-ticket-star halloween-ticket-star-tr" aria-hidden="true">★</span>
              <span className="halloween-ticket-star halloween-ticket-star-bl" aria-hidden="true">★</span>
              <span className="halloween-ticket-star halloween-ticket-star-br" aria-hidden="true">★</span>
              <span className="halloween-ticket-content">
                <span className="halloween-ticket-icon" aria-hidden="true"><Skull size={22} /></span>
                <span className="halloween-ticket-label font-heading">
                  {tr(cfg.halloween_cta_label, cfg.halloween_cta_label_ja)}
                </span>
                {(cfg.halloween_cta_sublabel || cfg.halloween_cta_sublabel_ja) && (
                  <span className="halloween-ticket-sublabel font-sans">
                    <span className="halloween-ticket-sublabel-line" />
                    {tr(cfg.halloween_cta_sublabel, cfg.halloween_cta_sublabel_ja)}
                    <span className="halloween-ticket-sublabel-line" />
                  </span>
                )}
              </span>
            </span>

            {/* Right stub */}
            <span className="halloween-ticket-stub halloween-ticket-stub-right" aria-hidden="true">
              <span className="halloween-ticket-stub-num">{disappearedCount != null ? String(disappearedCount).padStart(6, '0') : '000000'}</span>
            </span>
          </button>
        </div>

        {/* Waiver widget — required prior to entry */}
        <div className="mt-3 flex flex-col sm:flex-row items-center justify-center gap-2 text-center">
          <div className="inline-flex items-center gap-1.5 font-heading text-[10px] sm:text-xs tracking-[0.18em] uppercase text-red-200">
            <ShieldAlert size={13} className="text-red-300" />
            {isJa ? '入場前に必須の同意書' : 'Waiver required prior to entry'}
          </div>
          <button
            type="button"
            onClick={() => openHHWaiver({})}
            data-testid="halloween-waiver-btn"
            className="inline-flex items-center gap-2 bg-black/60 hover:bg-red-900/80 border-2 border-red-500/60 hover:border-red-300 text-red-100 hover:text-white font-heading text-[10px] sm:text-xs tracking-[0.2em] uppercase px-4 py-1.5 rounded-full transition-all"
            style={{ boxShadow: '0 0 12px rgba(220,38,38,0.35)' }}
          >
            <WaiverStampIcon size={22} />
            {isJa ? '同意書に署名' : 'Sign Waiver'}
          </button>
        </div>
      </section>

      {/* Scary GIFs strip (scrolls normally below sticky banner) */}
      {gifs.length > 0 && (
        <section
          data-testid="halloween-gifs-section"
          className="relative border-b-2 border-red-600/50 overflow-hidden"
          style={{ background: 'linear-gradient(180deg, #0a0000 0%, #1a0303 50%, #0a0000 100%)' }}
        >
          <div className="pointer-events-none absolute inset-0 halloween-gifs-fog" />
          <div
            data-testid="halloween-gifs-grid"
            className="relative max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-10 grid grid-cols-3 gap-2 sm:gap-6"
          >
            {gifs.map((g, i) => (
              <figure
                key={i}
                data-testid={`halloween-gif-${i}`}
                className={`halloween-gif-tile halloween-gif-jitter-${i} relative rounded-xl overflow-hidden`}
              >
                <div className="halloween-gif-frame relative aspect-square sm:aspect-[3/4] w-full overflow-hidden rounded-xl">
                  <img
                    src={g.url}
                    alt={g.label || `scary gif ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover halloween-gif-img"
                  />
                  <div className="halloween-gif-scanline pointer-events-none absolute inset-0" />
                  <div className="halloween-gif-vignette pointer-events-none absolute inset-0" />
                </div>
              </figure>
            ))}
          </div>
        </section>
      )}

      <style>{`
        .halloween-flicker {
          background: radial-gradient(circle at 30% 50%, rgba(255,120,0,0.15) 0%, transparent 60%),
                      radial-gradient(circle at 70% 50%, rgba(255,80,0,0.12) 0%, transparent 60%);
          animation: hFlicker 3.2s infinite ease-in-out;
        }
        @keyframes hFlicker {
          0%, 100% { opacity: 1; }
          8% { opacity: 0.65; }
          10% { opacity: 1; }
          40% { opacity: 0.85; }
          50% { opacity: 1; }
          72% { opacity: 0.55; }
          75% { opacity: 1; }
        }
        .halloween-title {
          color: #ffb347;
          -webkit-text-stroke: 0.5px #1a0000;
          paint-order: stroke fill;
          text-shadow:
            0 0 3px #ff8500,
            0 0 8px #ff6a00,
            0 0 18px rgba(255, 85, 0, 0.6);
          animation: hTitleGlow 2.4s infinite ease-in-out;
          letter-spacing: 0.04em;
          text-wrap: balance;
          word-spacing: 0.05em;
        }
        @media (min-width: 640px) {
          .halloween-title {
            color: #ff6a00;
            -webkit-text-stroke: 1.5px #1a0000;
            letter-spacing: 0.08em;
            word-spacing: 0.1em;
            text-shadow:
              0 0 4px #ff8500,
              0 0 12px #ff6a00,
              0 0 24px #ff5500,
              0 0 48px #ff4400,
              0 0 96px rgba(255, 90, 0, 0.6);
          }
        }
        @keyframes hTitleGlow {
          0%, 100% {
            text-shadow: 0 0 3px #ff8500, 0 0 8px #ff6a00, 0 0 18px rgba(255,85,0,0.6);
          }
          50% {
            text-shadow: 0 0 5px #ffb347, 0 0 14px #ff7a00, 0 0 24px rgba(255,90,0,0.8);
          }
        }
        @media (min-width: 640px) {
          @keyframes hTitleGlow {
            0%, 100% {
              text-shadow: 0 0 4px #ff8500, 0 0 12px #ff6a00, 0 0 24px #ff5500, 0 0 48px #ff4400, 0 0 96px rgba(255,90,0,0.6);
            }
            50% {
              text-shadow: 0 0 8px #ffb347, 0 0 18px #ff7a00, 0 0 32px #ff5a00, 0 0 60px #ff3500, 0 0 120px rgba(255,90,0,0.8);
            }
          }
        }
        /* ============================================================
           CARNIVAL TICKET CTA — "BOOK THE FRIGHT"
           Themable via .halloween-ticket-theme-* on the root button
           ============================================================ */
        .halloween-ticket-cta {
          display: inline-flex;
          align-items: stretch;
          font-family: inherit;
          padding: 0;
          border: 0;
          background: transparent;
          cursor: pointer;
          filter: drop-shadow(0 4px 10px rgba(0,0,0,0.55));
          transition: transform 220ms ease, filter 220ms ease;
          animation: halloween-ticket-idle 4.2s ease-in-out infinite;
        }
        .halloween-ticket-cta:hover {
          animation: halloween-ticket-wiggle 480ms ease-in-out infinite;
          transform: translateY(-2px) scale(1.04);
        }
        .halloween-ticket-cta:active { transform: translateY(0) scale(0.98); }
        .halloween-ticket-cta:focus-visible { outline: none; }
        .halloween-ticket-cta:focus-visible .halloween-ticket-body { box-shadow: 0 0 0 3px #fff, 0 0 0 6px var(--ticket-glow, #ff3200); }

        @keyframes halloween-ticket-idle {
          0%, 100% { transform: rotate(-0.6deg); }
          50%      { transform: rotate(0.6deg); }
        }
        @keyframes halloween-ticket-wiggle {
          0%   { transform: rotate(-2deg)  translateY(-2px) scale(1.04); }
          25%  { transform: rotate(1.8deg) translateY(-2px) scale(1.04); }
          50%  { transform: rotate(-1.5deg)translateY(-3px) scale(1.05); }
          75%  { transform: rotate(1.5deg) translateY(-2px) scale(1.04); }
          100% { transform: rotate(-2deg)  translateY(-2px) scale(1.04); }
        }

        /* Ticket stubs (side tabs) with notched inner edge */
        .halloween-ticket-stub {
          position: relative;
          background: var(--ticket-stub, #1c2b6b);
          color: var(--ticket-stub-text, #ffe08a);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 10px;
          min-width: 26px;
          font-family: 'Courier New', ui-monospace, monospace;
          font-weight: 900;
          font-size: 10px;
          letter-spacing: 0.12em;
          border-top: 3px solid var(--ticket-stub-edge, #0f1a4a);
          border-bottom: 3px solid var(--ticket-stub-edge, #0f1a4a);
          transition: filter 220ms ease;
        }
        .halloween-ticket-stub-num {
          writing-mode: vertical-rl;
          transform: rotate(180deg);
          text-shadow: 0 1px 0 rgba(0,0,0,0.5);
          filter: drop-shadow(0 0 3px var(--ticket-glow, rgba(255,80,0,0.7)));
          font-variant-numeric: tabular-nums;
        }
        /* Notched inner edge using dotted border via mask */
        .halloween-ticket-stub-left {
          border-radius: 6px 0 0 6px;
          -webkit-mask: radial-gradient(circle 4px at 100% 8px, transparent 98%, #000 100%) 0 0/100% 16px repeat-y;
                  mask: radial-gradient(circle 4px at 100% 8px, transparent 98%, #000 100%) 0 0/100% 16px repeat-y;
        }
        .halloween-ticket-stub-right {
          border-radius: 0 6px 6px 0;
          -webkit-mask: radial-gradient(circle 4px at 0% 8px, transparent 98%, #000 100%) 0 0/100% 16px repeat-y;
                  mask: radial-gradient(circle 4px at 0% 8px, transparent 98%, #000 100%) 0 0/100% 16px repeat-y;
        }

        /* Main body */
        .halloween-ticket-body {
          position: relative;
          overflow: hidden;
          padding: 10px 16px;
          background: var(--ticket-body, #fff5e0);
          color: var(--ticket-body-text, #b31b1b);
          border-top: 3px solid var(--ticket-body-edge, #d43a2a);
          border-bottom: 3px solid var(--ticket-body-edge, #d43a2a);
          box-shadow: inset 0 0 24px rgba(0,0,0,0.15), 0 0 12px var(--ticket-glow, rgba(255,80,0,0.6));
          transition: box-shadow 240ms ease;
          animation: halloween-ticket-glow 2.4s ease-in-out infinite;
        }
        @media (min-width: 640px) {
          .halloween-ticket-body { padding: 12px 22px; }
        }
        @keyframes halloween-ticket-glow {
          0%, 100% { box-shadow: inset 0 0 24px rgba(0,0,0,0.15), 0 0 12px var(--ticket-glow, rgba(255,80,0,0.6)); }
          50%      { box-shadow: inset 0 0 24px rgba(0,0,0,0.15), 0 0 28px var(--ticket-glow, rgba(255,80,0,0.9)), 0 0 48px var(--ticket-glow, rgba(255,80,0,0.5)); }
        }
        .halloween-ticket-cta:hover .halloween-ticket-body {
          box-shadow: inset 0 0 24px rgba(0,0,0,0.2), 0 0 34px var(--ticket-glow, rgba(255,80,0,1)), 0 0 60px var(--ticket-glow, rgba(255,80,0,0.6));
        }

        /* Starburst rays behind the label */
        .halloween-ticket-burst {
          position: absolute;
          inset: 0;
          background:
            repeating-conic-gradient(
              from 0deg at 50% 50%,
              var(--ticket-burst-a, #ffe4c8) 0deg,
              var(--ticket-burst-a, #ffe4c8) 8deg,
              var(--ticket-burst-b, #ff3a2e) 8deg,
              var(--ticket-burst-b, #ff3a2e) 16deg
            );
          opacity: var(--ticket-burst-opacity, 0.55);
          pointer-events: none;
          animation: halloween-ticket-burst-spin 22s linear infinite;
        }
        .halloween-ticket-cta:hover .halloween-ticket-burst {
          animation-duration: 6s;
          opacity: calc(var(--ticket-burst-opacity, 0.55) + 0.15);
        }
        @keyframes halloween-ticket-burst-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        /* Corner stars */
        .halloween-ticket-star {
          position: absolute;
          color: var(--ticket-star, #ffb800);
          font-size: 12px;
          text-shadow: 0 0 6px rgba(0,0,0,0.35), 0 0 10px var(--ticket-star, #ffb800);
          pointer-events: none;
          animation: halloween-ticket-twinkle 1.8s ease-in-out infinite;
        }
        .halloween-ticket-star-tl { top: 5px;  left: 10px; }
        .halloween-ticket-star-tr { top: 5px;  right: 10px; animation-delay: 0.4s; }
        .halloween-ticket-star-bl { bottom: 5px; left: 10px; animation-delay: 0.8s; }
        .halloween-ticket-star-br { bottom: 5px; right: 10px; animation-delay: 1.2s; }
        @keyframes halloween-ticket-twinkle {
          0%, 100% { opacity: 0.55; transform: scale(1)   rotate(0deg); }
          50%      { opacity: 1;    transform: scale(1.35) rotate(72deg); }
        }

        /* Content stack */
        .halloween-ticket-content {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 3px;
          text-align: center;
          line-height: 1;
        }
        .halloween-ticket-icon {
          color: var(--ticket-body-text, #b31b1b);
          margin-bottom: 2px;
          filter: drop-shadow(0 1px 0 rgba(255,255,255,0.5));
          animation: halloween-ticket-skull-bounce 3s ease-in-out infinite;
        }
        @keyframes halloween-ticket-skull-bounce {
          0%, 100% { transform: translateY(0) rotate(-4deg); }
          50%      { transform: translateY(-2px) rotate(4deg); }
        }
        .halloween-ticket-label {
          font-weight: 900;
          font-size: 15px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--ticket-body-text, #b31b1b);
          text-shadow:
            2px 2px 0 var(--ticket-label-stroke, #fff5e0),
            -1px -1px 0 var(--ticket-label-stroke, #fff5e0),
            0 0 10px var(--ticket-glow, rgba(255,80,0,0.5));
          -webkit-text-stroke: 0.5px var(--ticket-label-stroke, #fff5e0);
          padding: 0 4px;
          white-space: nowrap;
        }
        @media (min-width: 640px) {
          .halloween-ticket-label { font-size: 22px; padding: 0 6px; letter-spacing: 0.14em; }
        }
        .halloween-ticket-sublabel {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 9px;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: var(--ticket-body-text, #b31b1b);
          opacity: 0.85;
          margin-top: 3px;
        }
        .halloween-ticket-sublabel-line {
          display: inline-block;
          width: 18px;
          height: 1px;
          background: var(--ticket-body-text, #b31b1b);
          opacity: 0.6;
        }

        /* ============================================================
           THEME: neon-red (matches site aesthetic)
           ============================================================ */
        .halloween-ticket-theme-neon-red {
          --ticket-stub: #0a0000;
          --ticket-stub-text: #ff3a3a;
          --ticket-stub-edge: #ff0000;
          --ticket-body: #150000;
          --ticket-body-edge: #ff2a2a;
          --ticket-body-text: #ff4444;
          --ticket-burst-a: #1a0000;
          --ticket-burst-b: #ff2a2a;
          --ticket-burst-opacity: 0.35;
          --ticket-star: #ffb800;
          --ticket-glow: rgba(255, 30, 30, 0.85);
          --ticket-label-stroke: #0a0000;
        }
        .halloween-ticket-theme-neon-red .halloween-ticket-label {
          color: #ff2a2a;
          text-shadow:
            0 0 4px #ff1a1a,
            0 0 12px #ff0000,
            0 0 22px rgba(255, 0, 0, 0.8),
            1px 1px 0 #0a0000;
        }
        .halloween-ticket-theme-neon-red .halloween-ticket-icon { color: #ff2a2a; filter: drop-shadow(0 0 6px #ff0000); }
        .halloween-ticket-theme-neon-red .halloween-ticket-sublabel { color: #ff8080; }
        .halloween-ticket-theme-neon-red .halloween-ticket-sublabel-line { background: #ff2a2a; }

        /* ============================================================
           THEME: halloween (orange + purple + gold)
           ============================================================ */
        .halloween-ticket-theme-halloween {
          --ticket-stub: #3d0a5c;
          --ticket-stub-text: #ffcf3d;
          --ticket-stub-edge: #240438;
          --ticket-body: #ff6a00;
          --ticket-body-edge: #3d0a5c;
          --ticket-body-text: #240438;
          --ticket-burst-a: #ffb84d;
          --ticket-burst-b: #ff4400;
          --ticket-burst-opacity: 0.65;
          --ticket-star: #ffd23f;
          --ticket-glow: rgba(255, 90, 0, 0.85);
          --ticket-label-stroke: #ffe4a8;
        }
        .halloween-ticket-theme-halloween .halloween-ticket-label {
          color: #240438;
          text-shadow:
            2px 2px 0 #ffd23f,
            -1px -1px 0 #ffd23f,
            0 0 12px rgba(255,140,0,0.4);
        }

        /* ============================================================
           THEME: classic (red starburst + navy tabs — like the reference)
           ============================================================ */
        .halloween-ticket-theme-classic {
          --ticket-stub: #1c2b6b;
          --ticket-stub-text: #ffe08a;
          --ticket-stub-edge: #0f1a4a;
          --ticket-body: #fff5e0;
          --ticket-body-edge: #d43a2a;
          --ticket-body-text: #b31b1b;
          --ticket-burst-a: #fff5e0;
          --ticket-burst-b: #ff3a2e;
          --ticket-burst-opacity: 0.6;
          --ticket-star: #ffb800;
          --ticket-glow: rgba(255, 80, 0, 0.6);
          --ticket-label-stroke: #fff5e0;
        }

        /* ============================================================
           THEME: blood (dark blood-red + charcoal)
           ============================================================ */
        .halloween-ticket-theme-blood {
          --ticket-stub: #0a0000;
          --ticket-stub-text: #ff8080;
          --ticket-stub-edge: #440000;
          --ticket-body: #4a0000;
          --ticket-body-edge: #0a0000;
          --ticket-body-text: #ffe0e0;
          --ticket-burst-a: #2a0000;
          --ticket-burst-b: #7a0000;
          --ticket-burst-opacity: 0.55;
          --ticket-star: #ffb800;
          --ticket-glow: rgba(180, 0, 0, 0.9);
          --ticket-label-stroke: #0a0000;
        }
        .halloween-ticket-theme-blood .halloween-ticket-label {
          color: #ffe0e0;
          text-shadow:
            0 0 4px #ff2a2a,
            0 0 14px rgba(255, 0, 0, 0.7),
            2px 2px 0 #0a0000;
        }
        .halloween-ticket-theme-blood .halloween-ticket-icon { color: #ff4a4a; filter: drop-shadow(0 0 4px #ff0000); }

        /* ============================================================
           THEME: gold (gold starburst + black tabs + red text)
           ============================================================ */
        .halloween-ticket-theme-gold {
          --ticket-stub: #0a0a0a;
          --ticket-stub-text: #ffd23f;
          --ticket-stub-edge: #3a2a00;
          --ticket-body: #ffcf3d;
          --ticket-body-edge: #0a0a0a;
          --ticket-body-text: #8a0000;
          --ticket-burst-a: #fff2a8;
          --ticket-burst-b: #ff9500;
          --ticket-burst-opacity: 0.55;
          --ticket-star: #8a0000;
          --ticket-glow: rgba(255, 200, 40, 0.85);
          --ticket-label-stroke: #fff2a8;
        }

        /* Classic Halloween: pumpkin-orange body + jet-black stubs — high-contrast pop */
        .halloween-ticket-theme-orange-black {
          --ticket-stub: #0a0a0a;
          --ticket-stub-text: #ff9425;
          --ticket-stub-edge: #331100;
          --ticket-body: #ff7a1a;
          --ticket-body-edge: #0a0a0a;
          --ticket-body-text: #0a0a0a;
          --ticket-burst-a: #ffb56a;
          --ticket-burst-b: #ff4d00;
          --ticket-burst-opacity: 0.55;
          --ticket-star: #0a0a0a;
          --ticket-glow: rgba(255, 122, 26, 0.85);
          --ticket-label-stroke: #ffdcae;
        }
        .halloween-ticket-theme-orange-black .halloween-ticket-label {
          color: #0a0a0a;
          text-shadow: 0 0 10px rgba(255,255,255,0.35);
          -webkit-text-stroke: 0.6px #ffdcae;
        }
        .halloween-ticket-theme-orange-black .halloween-ticket-icon { color: #ffefdc; filter: drop-shadow(0 0 6px #ff7a1a); }
        .halloween-ticket-theme-orange-black .halloween-ticket-sublabel { color: #ffefdc; }
        .halloween-ticket-theme-orange-black .halloween-ticket-sublabel-line { background: #ffefdc; }

        /* White & Red: crisp arcade ticket — bone-white body with candy-apple red stubs */
        .halloween-ticket-theme-white-red {
          --ticket-stub: #ff1a1a;
          --ticket-stub-text: #ffffff;
          --ticket-stub-edge: #7a0000;
          --ticket-body: #fdfdfd;
          --ticket-body-edge: #ff1a1a;
          --ticket-body-text: #b40000;
          --ticket-burst-a: #ffe4e4;
          --ticket-burst-b: #ff3a3a;
          --ticket-burst-opacity: 0.55;
          --ticket-star: #b40000;
          --ticket-glow: rgba(255, 60, 60, 0.85);
          --ticket-label-stroke: #ffe4e4;
        }
        .halloween-ticket-theme-white-red .halloween-ticket-label {
          color: #b40000;
          text-shadow: 0 0 6px rgba(255,255,255,0.55);
          -webkit-text-stroke: 0.4px #ffe4e4;
        }
        .halloween-ticket-theme-white-red .halloween-ticket-icon { color: #ff1a1a; filter: drop-shadow(0 0 4px #ff0000); }
        .halloween-ticket-theme-white-red .halloween-ticket-sublabel { color: #b40000; }
        .halloween-ticket-theme-white-red .halloween-ticket-sublabel-line { background: #b40000; }

        /* Ghost White: eerie porcelain — cool white body with soft gray stubs and a pale glow */
        .halloween-ticket-theme-ghost {
          --ticket-stub: #d0d0d0;
          --ticket-stub-text: #2a2a2a;
          --ticket-stub-edge: #7a7a7a;
          --ticket-body: #fbfbfb;
          --ticket-body-edge: #b0b0b0;
          --ticket-body-text: #2a2a2a;
          --ticket-burst-a: #ffffff;
          --ticket-burst-b: #c9c9c9;
          --ticket-burst-opacity: 0.55;
          --ticket-star: #2a2a2a;
          --ticket-glow: rgba(220, 220, 220, 0.9);
          --ticket-label-stroke: #ffffff;
        }
        .halloween-ticket-theme-ghost .halloween-ticket-label {
          color: #1a1a1a;
          text-shadow: 0 0 8px rgba(255,255,255,0.9);
          -webkit-text-stroke: 0.4px #ffffff;
        }
        .halloween-ticket-theme-ghost .halloween-ticket-icon { color: #4a4a4a; filter: drop-shadow(0 0 5px #ffffff); }
        .halloween-ticket-theme-ghost .halloween-ticket-sublabel { color: #3a3a3a; }
        .halloween-ticket-theme-ghost .halloween-ticket-sublabel-line { background: #3a3a3a; }

        .halloween-blood-svg {
          filter: drop-shadow(0 4px 12px rgba(120, 0, 0, 0.6)) drop-shadow(0 8px 24px rgba(60, 0, 0, 0.4));
          animation: halloween-blood-sway 6s infinite ease-in-out;
        }
        @keyframes halloween-blood-sway {
          0%, 100% { transform: translateY(0) scaleY(1); }
          50%      { transform: translateY(2px) scaleY(1.03); }
        }
        .halloween-drop {
          transform-origin: center top;
          animation: halloween-drop-fall 3.2s infinite ease-in;
        }
        .halloween-drop-0 { animation-delay: 0.0s;  animation-duration: 3.4s; }
        .halloween-drop-1 { animation-delay: 0.9s;  animation-duration: 2.8s; }
        .halloween-drop-2 { animation-delay: 1.6s;  animation-duration: 3.6s; }
        .halloween-drop-3 { animation-delay: 0.4s;  animation-duration: 2.5s; }
        .halloween-drop-4 { animation-delay: 2.1s;  animation-duration: 3.2s; }
        .halloween-drop-5 { animation-delay: 1.2s;  animation-duration: 2.9s; }
        .halloween-drop-6 { animation-delay: 0.7s;  animation-duration: 3.7s; }
        @keyframes halloween-drop-fall {
          0%   { transform: translateY(0)   scaleY(0.6); opacity: 0; }
          10%  { transform: translateY(4px) scaleY(1);   opacity: 0.9; }
          70%  { transform: translateY(60px) scaleY(1.4); opacity: 0.9; }
          100% { transform: translateY(80px) scaleY(1.6); opacity: 0; }
        }
        .halloween-dismiss {
          background: rgba(10, 0, 0, 0.55);
          border: 1px solid rgba(255, 140, 0, 0.5);
          box-shadow: 0 0 8px rgba(255, 90, 0, 0.4), inset 0 0 6px rgba(255, 60, 0, 0.25);
          transition: transform 180ms ease, background 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
          cursor: pointer;
        }
        .halloween-dismiss:hover {
          background: rgba(60, 0, 0, 0.85);
          border-color: rgba(255, 90, 0, 0.9);
          box-shadow: 0 0 14px rgba(255, 90, 0, 0.8), inset 0 0 10px rgba(255, 40, 0, 0.45);
          transform: scale(1.08) rotate(-8deg);
        }
        @keyframes halloween-icon-swap {
          0% { transform: rotateY(-90deg); opacity: 0; }
          60% { transform: rotateY(10deg); opacity: 1; }
          100% { transform: rotateY(0deg); opacity: 1; }
        }
        .halloween-dismiss-icon {
          animation: halloween-icon-swap 260ms ease-out;
        }
        .halloween-live-actors {
          color: #fff2c8;
          background: linear-gradient(135deg, rgba(150, 0, 0, 0.75) 0%, rgba(255, 60, 0, 0.65) 100%);
          border: 1px solid rgba(255, 180, 90, 0.7);
          box-shadow: 0 0 10px rgba(255, 90, 0, 0.55), inset 0 0 6px rgba(255, 200, 0, 0.25);
          text-shadow: 0 0 6px rgba(255, 200, 0, 0.7);
        }
        .halloween-live-dot {
          display: inline-block; width: 7px; height: 7px; border-radius: 999px;
          background: #ff4a00;
          box-shadow: 0 0 6px #ff2a00, 0 0 14px #ff2a00;
          animation: hLivePulse 1s infinite ease-in-out;
        }
        @keyframes hLivePulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.6); opacity: 0.7; }
        }
        .halloween-disclaimer {
          text-shadow: 0 0 8px rgba(120, 0, 0, 0.6);
          letter-spacing: 0.02em;
        }
        .halloween-disappeared {
          color: #ffbfa8;
        }
        .halloween-disappeared-label {
          color: #ff9068;
          text-shadow: 0 0 8px rgba(255, 60, 0, 0.5);
        }
        .halloween-disappeared-num {
          color: #ff2a2a;
          text-shadow:
            0 0 4px #ff1a1a,
            0 0 10px #b30000,
            0 0 20px rgba(120, 0, 0, 0.7);
          transition: transform 300ms ease;
        }
        .halloween-disappeared-bump .halloween-disappeared-num {
          animation: halloween-count-bump 700ms ease-out;
        }
        @keyframes halloween-count-bump {
          0%   { transform: scale(1);   color: #ff2a2a; }
          20%  { transform: scale(1.55) rotate(-4deg); color: #ffe066; text-shadow: 0 0 8px #fff2a8, 0 0 22px #ff8500, 0 0 40px #ff2a00; }
          70%  { transform: scale(1.05) rotate(0deg); color: #ff8500; }
          100% { transform: scale(1);   color: #ff2a2a; }
        }
        .halloween-icon-pulse {
          animation: hPulse 2s infinite ease-in-out;
        }
        @keyframes hPulse {
          0%, 100% { transform: scale(1) rotate(-2deg); }
          50%      { transform: scale(1.08) rotate(2deg); }
        }
        .halloween-countdown-pill {
          color: #ffdca8;
          background: linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(50,10,0,0.75) 50%, rgba(0,0,0,0.55) 100%);
          border: 1px solid rgba(255,140,0,0.55);
          box-shadow:
            0 0 12px rgba(255,120,0,0.55),
            inset 0 0 12px rgba(255,90,0,0.25);
          text-shadow: 0 0 6px rgba(255,140,0,0.8);
          animation: hCountdownGlow 2.6s infinite ease-in-out;
        }
        @keyframes hCountdownGlow {
          0%, 100% { box-shadow: 0 0 12px rgba(255,120,0,0.55), inset 0 0 12px rgba(255,90,0,0.25); }
          50%      { box-shadow: 0 0 22px rgba(255,140,0,0.9), inset 0 0 16px rgba(255,110,0,0.4); }
        }
        .halloween-countdown-dot {
          display: inline-block;
          width: 8px; height: 8px; border-radius: 999px;
          background: #ff5a00;
          box-shadow: 0 0 8px #ff5a00, 0 0 18px #ff2a00;
          animation: hDotPulse 1.2s infinite ease-in-out;
        }
        @keyframes hDotPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.7; }
        }
        /* --- Scary GIF tiles --- */
        .halloween-gifs-fog {
          background:
            radial-gradient(ellipse at 20% 30%, rgba(120,0,0,0.35) 0%, transparent 55%),
            radial-gradient(ellipse at 80% 70%, rgba(80,0,0,0.30) 0%, transparent 55%);
          animation: hFogDrift 12s infinite ease-in-out alternate;
        }
        @keyframes hFogDrift {
          0% { transform: translateX(-6px); opacity: 0.85; }
          100% { transform: translateX(6px); opacity: 1; }
        }
        .halloween-gif-tile {
          background: #0a0000;
          border: 2px solid rgba(255, 30, 30, 0.55);
          box-shadow:
            0 0 14px rgba(255, 20, 20, 0.6),
            0 0 32px rgba(220, 0, 0, 0.4),
            inset 0 0 20px rgba(120, 0, 0, 0.5);
          transition: box-shadow 220ms ease, transform 220ms ease, border-color 220ms ease;
        }
        .halloween-gif-tile:hover {
          border-color: rgba(255, 60, 60, 0.95);
          box-shadow:
            0 0 26px rgba(255, 30, 30, 1),
            0 0 54px rgba(255, 0, 0, 0.6),
            inset 0 0 24px rgba(180, 0, 0, 0.6);
          transform: translateY(-2px) scale(1.02);
        }
        .halloween-gif-img {
          filter: contrast(1.15) saturate(0.85) brightness(0.92) hue-rotate(-8deg);
          transition: filter 220ms ease, transform 400ms ease;
        }
        .halloween-gif-tile:hover .halloween-gif-img {
          filter: contrast(1.25) saturate(1.1) brightness(1.02) hue-rotate(-12deg);
          transform: scale(1.05);
        }
        .halloween-gif-scanline {
          background: repeating-linear-gradient(
            to bottom,
            rgba(0,0,0,0.0) 0px,
            rgba(0,0,0,0.0) 2px,
            rgba(0,0,0,0.28) 3px,
            rgba(0,0,0,0.0) 4px
          );
          mix-blend-mode: multiply;
          opacity: 0.55;
        }
        .halloween-gif-vignette {
          background:
            radial-gradient(ellipse at center, transparent 45%, rgba(60,0,0,0.55) 85%, rgba(20,0,0,0.9) 100%),
            linear-gradient(180deg, rgba(120,0,0,0.15) 0%, transparent 40%, rgba(120,0,0,0.2) 100%);
        }
        .halloween-gif-label {
          color: #ff5a5a;
          background: rgba(0, 0, 0, 0.85);
          padding: 4px 6px;
          border-radius: 5px;
          border: 1px solid rgba(255, 30, 30, 0.4);
          text-shadow:
            0 1px 2px #000,
            0 0 4px #ff1a1a;
          letter-spacing: 0.16em;
          -webkit-text-stroke: 0.3px #000;
          font-weight: 700;
        }
        @media (min-width: 640px) {
          .halloween-gif-label {
            color: #ff2a2a;
            padding: 5px 8px;
            border-radius: 6px;
            border: none;
            background: linear-gradient(180deg, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.8) 100%);
            letter-spacing: 0.28em;
            text-shadow: 0 0 4px #ff1a1a, 0 0 10px #b30000, 0 0 20px #7a0000;
            -webkit-text-stroke: 0;
            font-weight: 400;
          }
        }
        .halloween-gif-jitter-0 { animation: hJitter 5.5s infinite steps(1); }
        .halloween-gif-jitter-1 { animation: hJitter2 6.3s infinite steps(1) 0.4s; }
        .halloween-gif-jitter-2 { animation: hJitter 7.1s infinite steps(1) 0.9s; }
        @keyframes hJitter {
          0%, 92%, 100% { transform: translate(0, 0) rotate(0deg); }
          93% { transform: translate(-2px, 1px) rotate(-0.6deg); }
          94% { transform: translate(2px, -1px) rotate(0.6deg); }
          95% { transform: translate(-1px, 2px) rotate(-0.4deg); }
          96% { transform: translate(1px, 0px) rotate(0.3deg); }
          97% { transform: translate(0, -2px) rotate(0deg); }
        }
        @keyframes hJitter2 {
          0%, 88%, 100% { transform: translate(0, 0) rotate(0deg); }
          89% { transform: translate(3px, -2px) rotate(0.8deg); }
          90% { transform: translate(-3px, 1px) rotate(-0.8deg); }
          91% { transform: translate(2px, 2px) rotate(0.5deg); }
          92% { transform: translate(-1px, -1px) rotate(-0.3deg); }
        }
      `}</style>
    </>
  );
}
